import axios from 'axios';

export type AssignSchedulePayload = {
  patientId: string | number;
  assignedUserId: Array<string | number>;
  startDateTime: string;
  endDateTime: string;
  visitType: string;
  visitPurpose: string;
  appointmentNotes: string;
  emergencyNotes: string;
};

export type PendingPatientUpdatePayload = {
  status?: string;
  urgency?: string;
  notes?: string;
  assignedUserId?: number;
  educatorId?: number;
  educatorIds?: number[];
  schedulerId?: number;
  virtualNpId?: number;
  externalId?: string;
};

export type PendingPatientGeocodePayload = {
  address: string;
  city: string;
  state: string;
  postalCode: string;
};

export type EducatorDistanceAddressResponse = {
  address: string;
  city: string;
  state: string;
  postalCode: string;
};

export type PatientImportMappingPayload = Record<string, string[]>;

/** Loads educator address for default distance calculation (backend endpoint TBD). */
export async function getEducatorDistanceAddress(
  educatorId: string | number
): Promise<any> {
  return await axios.get(
    `${process.env.VITE_API_URL}api/pending-patient/educator-address/${educatorId}`
  );
}

export async function createPendingManualPatient(patientData: any) {
  return axios.post(`${process.env.VITE_API_URL}api/pending-patient`, patientData);
}

export async function getPendingPatient(patientId: string | number) {
  return axios.get(`${process.env.VITE_API_URL}api/pending-patient/${patientId}`);
}

export async function updatePendingManualPatient(
  patientId: string | number,
  patientData: any
) {
  return axios.patch(`${process.env.VITE_API_URL}api/pending-patient/${patientId}`, patientData);
}

export async function submitAssignSchedule(payload: AssignSchedulePayload) {
  return axios.post(`${process.env.VITE_API_URL}api/pending-patient/calendar`, payload);
}

export type PendingPatientsQueryParams = {
  lat?: number;
  long?: number;
};

export async function getPendingPatients(cancelToken?: any, params?: PendingPatientsQueryParams) {
  return axios.get(`${process.env.VITE_API_URL}api/pending-patients`, {
    params,
    cancelToken
  });
}

export async function getDuplicatePendingPatients() {
  return axios.get(`${process.env.VITE_API_URL}api/pending-patient/get-duplicate`);
}

export type ValidateDuplicatePendingPatientsPayload = { pendingPatientIds: number[] };

export async function validateDuplicatePendingPatients(payload: ValidateDuplicatePendingPatientsPayload) {
  return axios.post(`${process.env.VITE_API_URL}api/pending-patient/validate-duplicate`, payload);
}

export async function mergePendingPatient(payload: {
  selected: { id: number | string; patientId: number | string };
  current: { id: number | string; patientId: number | string };
}) {
  return axios.post(`${process.env.VITE_API_URL}api/pending-patient/merge`, payload);
}

export async function updatePendingPatient(
  patientId: string | number,
  payload: PendingPatientUpdatePayload
) {
  return axios.patch(`${process.env.VITE_API_URL}api/pending-patient/${patientId}`, payload);
}

export async function geocodePendingPatientAddress(payload: PendingPatientGeocodePayload) {
  return axios.post(`${process.env.VITE_API_URL}api/pending-patient/geocode`, payload);
}

export async function getImportMapping() {
  return axios.get(`${process.env.VITE_API_URL}api/pending-patient/import-mapping`);
}

export async function updateImportMapping(payload: PatientImportMappingPayload) {
  return axios.put(`${process.env.VITE_API_URL}api/pending-patient/import-mapping`, payload);
}

export async function importPendingPatients(payload: any) {
  return axios.post(`${process.env.VITE_API_URL}api/pending-patient/import`, payload);
}

export async function getPriorityNotesUsers(patientId: number | string, isPending?: boolean) {
  const params: Record<string, any> = { patientId };
  if (isPending) {
    params.isPending = true;
  }
  return axios.get(`${process.env.VITE_API_URL}api/priority-notes/users`, {
    params
  });
}
